/**
 * Dedupe by Mongo `_id` or `id` (first occurrence wins).
 */
export function mergeProductListsById(...lists) {
  const map = new Map();
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const p of list) {
      if (!p || typeof p !== 'object') continue;
      const id = p._id ?? p.id;
      if (id == null) continue;
      const key = String(id);
      if (!map.has(key)) map.set(key, p);
    }
  }
  return Array.from(map.values());
}

function mergeNestedCatalogFields(obj) {
  if (!obj || typeof obj !== 'object') return null;
  const chunks = [];
  if (Array.isArray(obj.products)) chunks.push(obj.products);
  if (Array.isArray(obj.teamProducts)) chunks.push(obj.teamProducts);
  if (Array.isArray(obj.adminProducts)) chunks.push(obj.adminProducts);
  if (Array.isArray(obj.catalog)) chunks.push(obj.catalog);
  if (chunks.length === 0) return null;
  return mergeProductListsById(...chunks);
}

/**
 * Backend product list shapes vary: data as [], { products: [] }, { items: [] }, paginated { docs: [] }, etc.
 * Some APIs split admin vs team into `teamProducts` / `adminProducts` — merge those into one list.
 * Always return a real array for UI (.filter, .map, .length).
 */
export function normalizeProductsArray(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'object') {
    const mergedTop = mergeNestedCatalogFields(raw);
    if (mergedTop && mergedTop.length) return mergedTop;
    if (Array.isArray(raw.products)) return raw.products;
    if (Array.isArray(raw.items)) return raw.items;
    if (Array.isArray(raw.docs)) return raw.docs;
    if (Array.isArray(raw.data)) return raw.data;
    if (raw.data && typeof raw.data === 'object') {
      const mergedNested = mergeNestedCatalogFields(raw.data);
      if (mergedNested && mergedNested.length) return mergedNested;
      if (Array.isArray(raw.data.products)) return raw.data.products;
      if (Array.isArray(raw.data.items)) return raw.data.items;
      if (Array.isArray(raw.data.docs)) return raw.data.docs;
    }
  }
  return [];
}

function trendingLeaderboardRows(root, nested) {
  if (Array.isArray(root?.topProducts)) return root.topProducts;
  if (Array.isArray(nested?.topProducts)) return nested.topProducts;
  if (Array.isArray(root?.topByViews)) return root.topByViews;
  if (Array.isArray(nested?.topByViews)) return nested.topByViews;
  return [];
}

/**
 * Parse `GET /products/trending-by-views` (and alias most-viewed) envelopes.
 * Prefer top-level `products`; also read `data.products` and `topProducts` for merge fallbacks.
 */
export function parseTrendingByViewsResponse(response) {
  if (!response || typeof response !== 'object') {
    return { products: [], topProducts: [] };
  }
  const nested =
    response.data && typeof response.data === 'object' && !Array.isArray(response.data)
      ? response.data
      : null;

  let products = normalizeProductsArray(
    response.products ?? nested?.products ?? response.items ?? nested?.items
  );
  if (!products.length) {
    products = normalizeProductsArray(response);
  }

  return {
    products,
    topProducts: trendingLeaderboardRows(response, nested),
  };
}
